import { createSlice } from "@reduxjs/toolkit";

import {
  getAgeingAnalysisReportAction,
  getBatchSummaryReportAction,
  getBatchVoucherReportAction,
  getCostOfGoodsAction,
  getDayBookReportsAction,
  getDetailedBalanceSheetAction,
  getDetailedProfitLossStatementAction,
  getDetailedTrialBalanceAction,
  getDetailedNatureOfIncomeExpenseAction,
  getGeneralLedgerReportsAction,
  getMonthlyLedgerSummaryAction,
  getReceiptAndPaymentReportAction,
  getStockLimitAction,
  initialState,
  getLedgerStatementAction,
  getTrialBalanceAction,
  getProfitLossStatementAction,
  getBalanceSheetAction,
  getStockReportsSummaryAction,
  getStockStatementAction,
  reportsReducer,
  getGeneralLedgerDownloadHistoryAction,
  getIncomeExpenseProfitAction,
  getIncomeExpenseLedgerAction,
  getSaleseExpensePerformanceAction,
  getExpensePerformanceAction,
  emailOCRComparisonReportAction,
  getRelatedPartyTransactionReportAction,
} from "@/redux/reports";
import type { ReportsType } from "@/redux/reports/reportsType";

export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: reportsReducer,

  extraReducers: (builder) => {
    builder.addCase(getLedgerStatementAction.pending, (state: ReportsType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getLedgerStatementAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          ledgerStatement: result?.data,
          ledgerStatementCount: result?.total_count,
          closingBalance: result?.closing_balance,
          openingBalance: result?.opening_balance,
          totalDebitCreditAmount: result?.total_debit_credit_amount,
        };
      }
    );

    builder.addCase(
      getLedgerStatementAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(getTrialBalanceAction.pending, (state: ReportsType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getTrialBalanceAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          trialBalance: result?.data,
          trialBalanceCount: result?.total_count,
          totalBalances: {
            total_opening_balance: result?.total_opening_balance,
            total_closing_balance: result?.total_closing_balance,
            total_credit_amount: result?.total_credit_amount,
            total_debit_amount: result?.total_debit_amount,
          },
        };
      }
    );

    builder.addCase(
      getTrialBalanceAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getDetailedTrialBalanceAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getDetailedTrialBalanceAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          detailedTrialBalance: result,
        };
      }
    );

    builder.addCase(
      getDetailedTrialBalanceAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getProfitLossStatementAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getProfitLossStatementAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          expenseProfitLoss: result?.expenses,
          revenueProfitLoss: result?.income,
          totalProfitLossBalance: {
            total_expense: result?.total_expense,
            total_revenue: result?.total_revenue,
            net_profit: result?.net_profit,
          },
        };
      }
    );

    builder.addCase(
      getDetailedNatureOfIncomeExpenseAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getDetailedNatureOfIncomeExpenseAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          detailedNatureOfIncomeExpense: result,
        };
      }
    );

    builder.addCase(
      getDetailedNatureOfIncomeExpenseAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getProfitLossStatementAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getDetailedProfitLossStatementAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getDetailedProfitLossStatementAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          detailedProfitLossData: result,
        };
      }
    );

    builder.addCase(
      getDetailedProfitLossStatementAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getDetailedBalanceSheetAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getDetailedBalanceSheetAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          detailedBalanceSheetData: result,
        };
      }
    );

    builder.addCase(
      getDetailedBalanceSheetAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getStockReportsSummaryAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getStockReportsSummaryAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { data } = payload;
        if (data.success === 0) {
          return {
            ...state,
            fetching: false,
            success: data.success,
            statusCode: data.status_code,
            error: data.message,
          };
        }
        return {
          ...state,
          fetching: false,
          stockReportsSummaryData: data.data,
          totalStockReportsSummaryData: {
            total_inwards_value: data.total_inwards_value,
            total_closing_value: data.total_closing_value,
            total_opening_value: data.total_opening_value,
            total_outwards_value: data.total_outwards_value,
          },
        };
      }
    );

    builder.addCase(
      getStockReportsSummaryAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getStockStatementAction.pending, (state: ReportsType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getStockStatementAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { data } = payload;
        if (data.success === 0) {
          return {
            ...state,
            fetching: false,
            success: data.success,
            statusCode: data.status_code,
            error: data.message,
          };
        }
        return {
          ...state,
          fetching: false,
          stockStatementData: data.data,
          totalStockStatementData: {
            total_inwards_qty: data.total_inwards_qty,
            total_inwards_rate: data.total_inwards_rate,
            total_inwards_value: data.total_inwards_value,
            total_outwards_qty: data.total_outwards_qty,
            total_outwards_rate: data.total_outwards_rate,
            total_outwards_value: data.total_outwards_value,
            total_inwards_alt_qty: data.total_inwards_alt_qty,
            total_outwards_alt_qty: data.total_outwards_alt_qty,
          },
        };
      }
    );

    builder.addCase(
      getStockStatementAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getBalanceSheetAction.pending, (state: ReportsType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getBalanceSheetAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          assetsBalanceSheetData: result?.assests_json,
          liabilitiesBalanceSheetData: result?.liabilities_json,
          equityBalanceSheetData: result?.equity_json,
          paymentBalanceSheetData: result?.payment_json,
          supplierBalanceSheetData: result?.supplier_json,
          customerBalanceSheetData: result?.customer_json,
          taxPayableBalanceSheetData: result?.tax_payable_json,
          taxReceivableBalanceSheetData: result?.tax_receivable_json,
          stockSummaryBalanceSheetData: result?.stock_summary_json,
          balanceSheetTotal: {
            customer_total_value: result?.customer_total_value,
            supplier_total_value: result?.supplier_total_value,
            assets_total_value: result?.assests_total_value,
            liabilities_total_value: result?.liabilities_total_value,
            equity_total_value: result?.equity_total_value,
            payment_total_value: result?.payment_total_value,
            net_profit: result?.net_profit,
            tax_payable_value: result?.tax_payable_value,
            tax_receivable_value: result?.tax_receivable_value,
            total_stock_summary_value: result?.total_stock_summary_value,
          },
        };
      }
    );

    builder.addCase(
      getBalanceSheetAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getDayBookReportsAction.pending, (state: ReportsType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getDayBookReportsAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          dayBookListData: result.data,
        };
      }
    );

    builder.addCase(
      getDayBookReportsAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getGeneralLedgerReportsAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getGeneralLedgerReportsAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          generalLedgerListData: result,
        };
      }
    );

    builder.addCase(
      getGeneralLedgerReportsAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getCostOfGoodsAction.pending, (state: ReportsType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getCostOfGoodsAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          costOfGoods: result,
        };
      }
    );

    builder.addCase(
      getCostOfGoodsAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getAgeingAnalysisReportAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getAgeingAnalysisReportAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          ageingAnalysisList: result?.data,
          ageingAnalysisTotalCount: result?.total_count,
          ageingAnalysis0To30DaysTotal: result?.total_sum?.["0_to_30_days"],
          ageingAnalysis30To60DaysTotal: result?.total_sum?.["30_to_60_days"],
          ageingAnalysis60To90DaysTotal: result?.total_sum?.["60_to_90_days"],
          ageingAnalysis90To180DaysTotal: result?.total_sum?.["90_to_180_days"],
          ageingAnalysis180To365DaysTotal:
            result?.total_sum?.["180_to_365_days"],
          ageingAnalysisMoreThan365DaysTotal:
            result?.total_sum?.["more_than_365_days"],
          ageingAnalysisTotalOfTotal: result?.total_sum?.["total_amount"],
        };
      }
    );

    builder.addCase(
      getAgeingAnalysisReportAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getReceiptAndPaymentReportAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getReceiptAndPaymentReportAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          receiptAndPayment: result,
        };
      }
    );

    builder.addCase(
      getReceiptAndPaymentReportAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(getStockLimitAction.pending, (state: ReportsType) => ({
      ...state,
      fetching: true,
    }));

    builder.addCase(
      getStockLimitAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { data } = payload;
        if (data.success === 0) {
          return {
            ...state,
            fetching: false,
            success: data.success,
            statusCode: data.status_code,
            error: data.message,
          };
        }
        return {
          ...state,
          fetching: false,
          stocklimitData: data.result,
          stockLimitTotalCount: data.total_count,
        };
      }
    );

    builder.addCase(
      getStockLimitAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getBatchSummaryReportAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getBatchSummaryReportAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { data } = payload;
        if (data.success === 0) {
          return {
            ...state,
            fetching: false,
            success: data.success,
            statusCode: data.status_code,
            error: data.message,
          };
        }
        return {
          ...state,
          fetching: false,
          batchSummaryReportData: data.result,
          totalBatchSummaryData: {
            total_opening_balance: data.total_opening_balance,
            total_closing_balance: data.total_closing_balance,
            total_inwards_value: data.total_inwards_value,
            total_outwards_value: data.total_outwards_value,
            stock_item_name: data.stock_item_name,
          },
        };
      }
    );

    builder.addCase(
      getBatchSummaryReportAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getBatchVoucherReportAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getBatchVoucherReportAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { data } = payload;
        if (data.success === 0) {
          return {
            ...state,
            fetching: false,
            success: data.success,
            statusCode: data.status_code,
            error: data.message,
          };
        }
        return {
          ...state,
          fetching: false,
          batchVoucherData: data.data,
          totalBatchVoucherData: {
            opening_qty: data.opening_qty,
            opening_value: data.opening_value,
            stock_item_name: data.stock_item_name,
            closing_qty: data.closing_qty,
            closing_value: data.closing_value,
            total_inwards_qty: data.total_inwards_qty,
            total_inwards_rate: data.total_inwards_rate,
            total_inwards_value: data.total_inwards_value,
            total_outwards_qty: data.total_outwards_qty,
            total_outwards_rate: data.total_outwards_rate,
            total_outwards_value: data.total_outwards_value,
            batch_details: {
              batch_id: data.batch_id,
              batch_number: data.batch_number,
              manufacturing_date: data.manufacturing_date,
              expiry_date: data.expiry_date,
            },
            total_inwards_alt_qty: data.total_inwards_alt_qty,
            total_outwards_alt_qty: data.total_outwards_alt_qty,
          },
        };
      }
    );

    builder.addCase(
      getBatchVoucherReportAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getMonthlyLedgerSummaryAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getMonthlyLedgerSummaryAction.fulfilled,
      (state: ReportsType, { payload }) => {
        // @ts-ignore [TODO:] Need to remove this ts-ignore by giving proper type
        const { success, message, data, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }
        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          monthlyLedgerSummaryTotal: data?.result?.grand_totals,
          monthlyLedgerSummaryData: data?.result?.monthly_summary,
        };
      }
    );

    builder.addCase(
      getMonthlyLedgerSummaryAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        error: error?.message ?? null,
        success: 0,
      })
    );

    builder.addCase(
      getGeneralLedgerDownloadHistoryAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getGeneralLedgerDownloadHistoryAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload.data;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          generalLedgerDownloadHistory: result.data,
          generalLedgerDownloadHistoryTotalCount: result.total_count,
        };
      }
    );

    builder.addCase(
      getGeneralLedgerDownloadHistoryAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(
      getIncomeExpenseProfitAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getIncomeExpenseProfitAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload.data;

        if (success === 0) {
          return {
            ...state,
            incomeExpenseFetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          incomeExpenseFetching: false,
          incomeExpenseProfitList: result,
        };
      }
    );

    builder.addCase(
      getIncomeExpenseProfitAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        incomeExpenseFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(
      getSaleseExpensePerformanceAction.pending,
      (state: ReportsType) => ({
        ...state,
        salesPerformanceFetching: true,
      })
    );

    builder.addCase(
      getSaleseExpensePerformanceAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload.data;

        if (success === 0) {
          return {
            ...state,
            salesPerformanceFetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          salesPerformanceFetching: false,
          salesPerformanceList: result.data,
        };
      }
    );

    builder.addCase(
      getSaleseExpensePerformanceAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        salesPerformanceFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(
      getExpensePerformanceAction.pending,
      (state: ReportsType) => ({
        ...state,
        expensePerformanceFetching: true,
      })
    );

    builder.addCase(
      getExpensePerformanceAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload.data;

        if (success === 0) {
          return {
            ...state,
            expensePerformanceFetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          expensePerformanceFetching: false,
          expensePerformanceList: result.data,
        };
      }
    );

    builder.addCase(
      getExpensePerformanceAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        expensePerformanceFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
    builder.addCase(
      getIncomeExpenseLedgerAction.pending,
      (state: ReportsType) => ({
        ...state,
        ledgerInsightFetching: true,
      })
    );

    builder.addCase(
      getIncomeExpenseLedgerAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload.data;

        if (success === 0) {
          return {
            ...state,
            ledgerInsightFetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          ledgerInsightFetching: false,
          ledgerInsightData: result,
        };
      }
    );

    builder.addCase(
      getIncomeExpenseLedgerAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        ledgerInsightFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      emailOCRComparisonReportAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      emailOCRComparisonReportAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, status_code } = payload.data;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          success,
          statusCode: status_code,
        };
      }
    );

    builder.addCase(
      emailOCRComparisonReportAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getRelatedPartyTransactionReportAction.pending,
      (state: ReportsType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getRelatedPartyTransactionReportAction.fulfilled,
      (state: ReportsType, { payload }) => {
        const { success, message, result, status_code } = payload.data;

        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success,
            statusCode: status_code,
            error: message,
          };
        }

        return {
          ...state,
          fetching: false,
          statusCode: status_code,
          relatedPartyReportList: result?.data ?? [],
          relatedPartyReportTotalCount: Number(result?.total_count ?? 0),
          relatedPartyReportTotals: {
            total_opening_balance: result?.total_opening_balance,
            total_closing_balance: result?.total_closing_balance,
          },
        };
      }
    );

    builder.addCase(
      getRelatedPartyTransactionReportAction.rejected,
      (state: ReportsType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
  },
});

export const {
  resetLedgerStatement,
  resetTrialBalance,
  resetExpenseProfitLoss,
  resetRevenueProfitLoss,
  resetReportsError,
  resetReportsFetching,
  resetReportsSuccess,
  resetAssetsBalanceSheetData,
  resetCustomerBalanceSheetData,
  resetEquityBalanceSheetData,
  resetLiabilitiesBalanceSheetData,
  resetPaymentBalanceSheetData,
  resetSupplierBalanceSheetData,
  resetTaxPayableBalanceSheetData,
  resetTaxReceivableBalanceSheetData,
  resetStockReportsSummary,
  resetStockStatement,
  resetDetailedProfitLossStatement,
  resetTaxLedgerStatementCount,
  resetDetailedBalanceSheet,
  resetBalanceSheetTotal,
  resetDayBookListData,
  resetGeneralLedgerListData,
  resetCostOfGoodsData,
  resetAgeingAnalysisList,
  resetAgeingAnalysisTotalCount,
  resetStockSummaryBalanceSheetData,
  resetDetailedTrialBalance,
  resetReceiptAndPayment,
  resetStocklimitData,
  resetStockLimitTotalCount,
  resetBatchSummaryReportData,
  resetBatchVoucherReportData,
  resetMonthlyLedgerSummaryData,
  resetMonthlyLedgerSummaryTotal,
  resetGeneralLedgerDownloadHistory,
  resetExpensePerformanceList,
  resetSalesPerformanceList,
  resetIncomeExpenseProfitList,
  resetLedgerInsightData,
  resetRelatedPartyReport,
} = reportsSlice.actions;
